import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComingSoonBanner } from '../ComingSoonBanner';
import ProjectCard from './components/ProjectCard';
import ProjectListView from './components/ProjectListView';
import AddProjectModal from './components/AddProjectModal';
import EditProjectModal from './components/EditProjectModal';
import DeleteProjectModal from './components/DeleteProjectModal';
import ImportTasksModal from './components/ImportTasksModal';
import ExportTasksModal from './components/ExportTasksModal';
import Pagination from '../../../components/Pagination';

// Simple inline stubs for UI components
const Icon = ({ name, size = 16, className = '' }) => {
  const sizeStr = size + 'px';
  return <span className={className} style={{ display: 'inline-flex', width: sizeStr, height: sizeStr }} />;
};
const Button = ({ children, onClick, variant, disabled, className = '', iconName, size: btnSize, loading, ...rest }) => (
  <button onClick={onClick} disabled={disabled || loading} className={`px-4 py-2 rounded-lg font-medium transition-colors ${variant === 'outline' ? 'border border-border bg-card text-gray-700 hover:bg-gray-50' : 'bg-blue-600 text-white hover:bg-blue-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`} {...rest}>{loading ? 'Loading...' : children}</button>
);
const Input = ({ className = '', label, error, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <input className={`border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none ${className}`} {...props} />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);
const Select = ({ value, onChange, options, placeholder, className = '', label, error, disabled }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled} className={`border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none bg-card ${className}`}>
      {placeholder && <option value="">{placeholder}</option>}
      {options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const ProjectsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('card');

  // Mock project data with enhanced fields
  const mockProjects = [
    {
      id: 'PROJ001',
      name: 'HR System Modernization',
      taskCount: 45,
      completedTasks: 32,
      overdueTasks: 3,
      dateRange: { start: '2026-02-01', end: '2026-06-30' },
      status: 'Active',
      health: 'At Risk',
      teamMembers: 8,
      owner: { name: 'Sarah Johnson', initials: 'SJ' },
      budget: '$125,000',
      description: 'Complete overhaul of HR management system with modern cloud-based solutions',
      isMyProject: true
    },
    {
      id: 'PROJ002',
      name: 'Employee Wellness Program',
      taskCount: 28,
      completedTasks: 25,
      overdueTasks: 0,
      dateRange: { start: '2026-01-15', end: '2026-12-31' },
      status: 'Active',
      health: 'On Track',
      teamMembers: 5,
      owner: { name: 'Michael Chen', initials: 'MC' },
      budget: '$75,000',
      description: 'Launch comprehensive wellness program including fitness, mental health, and nutrition initiatives',
      isMyProject: false
    },
    {
      id: 'PROJ003',
      name: 'Office Expansion Dubai',
      taskCount: 62,
      completedTasks: 62,
      overdueTasks: 0,
      dateRange: { start: '2025-11-01', end: '2025-12-31' },
      status: 'Completed',
      health: 'On Track',
      teamMembers: 12,
      owner: { name: 'Ahmed Al-Mansoori', initials: 'AA' },
      budget: '$500,000',
      description: 'New office space setup in Dubai Marina with modern facilities and equipment',
      isMyProject: false
    },
    {
      id: 'PROJ004',
      name: 'Digital Marketing Campaign Q1',
      taskCount: 35,
      completedTasks: 0,
      overdueTasks: 0,
      dateRange: { start: '2026-03-01', end: '2026-05-31' },
      status: 'Upcoming',
      health: 'On Track',
      teamMembers: 6,
      owner: { name: 'Emily Rodriguez', initials: 'ER' },
      budget: '$90,000',
      description: 'Multi-channel digital marketing campaign targeting regional markets',
      isMyProject: true
    },
    {
      id: 'PROJ005',
      name: 'Product Launch - Smart Watch',
      taskCount: 58,
      completedTasks: 18,
      overdueTasks: 7,
      dateRange: { start: '2026-01-20', end: '2026-04-15' },
      status: 'Active',
      health: 'Blocked',
      teamMembers: 15,
      owner: { name: 'David Kim', initials: 'DK' },
      budget: '$250,000',
      description: 'Complete product launch including manufacturing, marketing, and distribution',
      isMyProject: true
    },
    {
      id: 'PROJ006',
      name: 'Legacy System Migration',
      taskCount: 42,
      completedTasks: 28,
      overdueTasks: 5,
      dateRange: { start: '2025-10-01', end: '2026-01-15' },
      status: 'On Hold',
      health: 'Blocked',
      teamMembers: 7,
      owner: { name: 'Lisa Anderson', initials: 'LA' },
      budget: '$180,000',
      description: 'Migration from legacy systems to modern cloud infrastructure - paused due to resource constraints',
      isMyProject: false
    },
    {
      id: 'PROJ007',
      name: 'Customer Portal Redesign',
      taskCount: 38,
      completedTasks: 0,
      overdueTasks: 0,
      dateRange: { start: '2026-04-01', end: '2026-07-31' },
      status: 'Upcoming',
      health: 'On Track',
      teamMembers: 9,
      owner: { name: 'Robert Taylor', initials: 'RT' },
      budget: '$140,000',
      description: 'Complete redesign of customer-facing portal with improved UX and new features',
      isMyProject: false
    },
    {
      id: 'PROJ008',
      name: 'Data Analytics Platform',
      taskCount: 52,
      completedTasks: 38,
      overdueTasks: 2,
      dateRange: { start: '2026-02-10', end: '2026-08-30' },
      status: 'Active',
      health: 'At Risk',
      teamMembers: 11,
      owner: { name: 'Jennifer Lee', initials: 'JL' },
      budget: '$320,000',
      description: 'Build comprehensive data analytics platform for business intelligence',
      isMyProject: true
    },
    {
      id: 'PROJ009',
      name: 'Mobile App Development',
      taskCount: 67,
      completedTasks: 67,
      overdueTasks: 0,
      dateRange: { start: '2025-12-01', end: '2026-01-15' },
      status: 'Completed',
      health: 'On Track',
      teamMembers: 10,
      owner: { name: 'Thomas Brown', initials: 'TB' },
      budget: '$200,000',
      description: 'Native mobile applications for iOS and Android platforms',
      isMyProject: false
    },
    {
      id: 'PROJ010',
      name: 'Sustainability Initiative 2026',
      taskCount: 31,
      completedTasks: 0,
      overdueTasks: 0,
      dateRange: { start: '2026-03-15', end: '2026-12-31' },
      status: 'Upcoming',
      health: 'On Track',
      teamMembers: 6,
      owner: { name: 'Maria Garcia', initials: 'MG' },
      budget: '$95,000',
      description: 'Company-wide sustainability program focusing on carbon reduction and waste management',
      isMyProject: false
    },
    {
      id: 'PROJ011',
      name: 'Training & Development Program',
      taskCount: 44,
      completedTasks: 35,
      overdueTasks: 0,
      dateRange: { start: '2026-01-10', end: '2026-06-30' },
      status: 'Active',
      health: 'On Track',
      teamMembers: 8,
      owner: { name: 'James Wilson', initials: 'JW' },
      budget: '$110,000',
      description: 'Comprehensive employee training program with certifications and skill development',
      isMyProject: false
    },
    {
      id: 'PROJ012',
      name: 'Supply Chain Optimization',
      taskCount: 49,
      completedTasks: 22,
      overdueTasks: 4,
      dateRange: { start: '2026-02-20', end: '2026-09-30' },
      status: 'Active',
      health: 'At Risk',
      teamMembers: 13,
      owner: { name: 'Patricia Martinez', initials: 'PM' },
      budget: '$280,000',
      description: 'End-to-end supply chain optimization with automation and AI integration',
      isMyProject: true
    }
  ];

  // Check if project needs attention
  const needsAttention = (project) => {
    return project?.overdueTasks > 0 || project?.health === 'Blocked' ||
           (project?.status === 'Active' && new Date(project?.dateRange?.end) < new Date());
  };

  // Filter projects by tab
  const tabFilteredProjects = useMemo(() => {
    let filtered = mockProjects;

    switch (activeTab) {
      case 'my':
        filtered = mockProjects?.filter(p => p?.isMyProject);
        break;
      case 'active':
        filtered = mockProjects?.filter(p => p?.status === 'Active');
        break;
      case 'attention':
        filtered = mockProjects?.filter(p => needsAttention(p));
        break;
      case 'completed':
        filtered = mockProjects?.filter(p => p?.status === 'Completed');
        break;
      default:
        filtered = mockProjects;
    }

    return filtered;
  }, [activeTab, mockProjects]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return tabFilteredProjects?.filter(project => {
      // Search filter
      const searchMatch = searchTerm === '' ||
        project?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        project?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        project?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      // Status filter
      const statusMatch = statusFilter === '' || project?.status === statusFilter;

      // Date range filter
      const dateMatch = (!dateRange?.start || project?.dateRange?.start >= dateRange?.start) &&
                       (!dateRange?.end || project?.dateRange?.end <= dateRange?.end);

      return searchMatch && statusMatch && dateMatch;
    });
  }, [searchTerm, statusFilter, dateRange, tabFilteredProjects]);

  // Pagination calculations
  const totalPages = Math.ceil((filteredProjects?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProjects = filteredProjects?.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange, activeTab]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const handleViewProject = (project) => {
    console.log('View project:', project);
  };

  const handleImportTasks = (project) => {
    setSelectedProject(project);
    setShowImportModal(true);
  };

  const handleExportTasks = (project) => {
    setSelectedProject(project);
    setShowExportModal(true);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const handleDeleteProject = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateRange({ start: '', end: '' });
  };

  const hasActiveFilters = searchTerm || statusFilter || dateRange?.start || dateRange?.end;

  const tabs = [
    { id: 'all', label: 'All Projects', icon: 'Briefcase' },
    { id: 'my', label: 'My Projects', icon: 'User' },
    { id: 'active', label: 'Active', icon: 'Activity' },
    { id: 'attention', label: 'Needs Attention', icon: 'AlertTriangle', urgent: true },
    { id: 'completed', label: 'Completed', icon: 'CheckCircle' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 pt-6">
        <ComingSoonBanner
          icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>}
          title="Projects List"
          description="Projects List is currently in development. This is a preview of the upcoming feature."
          gradientFrom="from-teal-500"
          gradientTo="to-emerald-500"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
              <p className="text-gray-600 mt-2">Manage company initiatives, ownership, and progress</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowAddModal(true)}
                variant="outline"
                disabled
                className="opacity-50 cursor-not-allowed"
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Import Projects
              </Button>
              <Button
                onClick={() => setShowAddModal(true)}
                iconName="Plus"
                size="lg"
              >
                Add Project
              </Button>
            </div>
          </div>
        </div>

        {/* Smart View Tabs */}
        <div className="mb-6">
          <div className="border-b border-border bg-card rounded-t-lg">
            <nav className="flex space-x-1 px-4" aria-label="Tabs">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200
                    ${activeTab === tab?.id
                      ? tab?.urgent
                        ? 'border-amber-500 text-amber-600' :'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-border'
                    }
                    ${tab?.urgent ? 'hover:text-amber-600' : ''}
                  `}
                >
                  <Icon
                    name={tab?.icon}
                    size={16}
                    className={`mr-2 ${tab?.urgent && activeTab !== tab?.id ? 'text-amber-500' : ''}`}
                  />
                  {tab?.label}
                  {tab?.id === 'attention' && (
                    <span className="ml-2 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {mockProjects?.filter(p => needsAttention(p))?.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Icon name="Filter" size={20} className="mr-2" />
              Filters
            </h2>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center text-sm text-primary hover:text-primary/80 font-medium"
              >
                <Icon name="X" size={14} className="mr-1" />
                Clear All
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <Input
                type="text"
                placeholder="Search projects, IDs, descriptions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full"
              />
            </div>

            {/* Status Filter */}
            <div>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'Upcoming', label: 'Upcoming' },
                  { value: 'Active', label: 'Active' },
                  { value: 'On Hold', label: 'On Hold' },
                  { value: 'Completed', label: 'Completed' }
                ]}
                placeholder="Status"
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                <Input
                  type="date"
                  value={dateRange?.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e?.target?.value }))}
                  placeholder="Start Date"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                <Input
                  type="date"
                  value={dateRange?.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e?.target?.value }))}
                  placeholder="End Date"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary & View Toggle */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{startIndex + 1}</span> to <span className="font-semibold">{Math.min(endIndex, filteredProjects?.length)}</span> of <span className="font-semibold">{filteredProjects?.length}</span> projects
          </p>
          <div className="flex items-center space-x-4">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-card border border-border rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  viewMode === 'card' ?'bg-primary text-white' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name="LayoutGrid" size={16} className="mr-1.5" />
                Card View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  viewMode === 'list' ?'bg-primary text-white' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name="List" size={16} className="mr-1.5" />
                Compact List
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Items per page:</span>
              <Select
                value={itemsPerPage?.toString()}
                onChange={handleItemsPerPageChange}
                options={[
                  { value: '6', label: '6' },
                  { value: '12', label: '12' },
                  { value: '18', label: '18' },
                  { value: '24', label: '24' }
                ]}
                className="w-20"
              />
            </div>
          </div>
        </div>

        {/* Empty States */}
        {mockProjects?.length === 0 ? (
          <div className="bg-card rounded-lg shadow-sm border border-border p-16 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Briefcase" size={40} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No projects yet</h3>
              <p className="text-gray-600 mb-8">
                Projects help organize cross-team initiatives and accountability. Get started by creating your first project.
              </p>
              <Button onClick={() => setShowAddModal(true)} size="lg" iconName="Plus">
                Create your first project
              </Button>
            </div>
          </div>
        ) : filteredProjects?.length === 0 ? (
          <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
            <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No projects match your filters</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search criteria or filters</p>
            <Button onClick={clearFilters} variant="outline">
              Clear Filters
            </Button>
          </div>
        ) : (
          <>
            {/* Card View */}
            {viewMode === 'card' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedProjects?.map(project => (
                  <ProjectCard
                    key={project?.id}
                    project={project}
                    needsAttention={needsAttention(project)}
                    onView={() => handleViewProject(project)}
                    onImport={() => handleImportTasks(project)}
                    onExport={() => handleExportTasks(project)}
                    onEdit={() => handleEditProject(project)}
                    onDelete={() => handleDeleteProject(project)}
                  />
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <ProjectListView
                projects={paginatedProjects}
                needsAttention={needsAttention}
                onView={handleViewProject}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
              />
            )}
          </>
        )}

        {/* Pagination Controls */}
        {filteredProjects?.length > 0 && totalPages > 1 && (
            <div className="mt-8">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    variant="full"
                />
            </div>
        )}
      </div>
      {/* Modals */}
      <AddProjectModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
      <EditProjectModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        project={selectedProject}
      />
      <DeleteProjectModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        project={selectedProject}
      />
      <ImportTasksModal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
      />
      <ExportTasksModal
        isOpen={showExportModal}
        onClose={() => {
          setShowExportModal(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
      />
    </div>
  );
};

export default ProjectsList;
